import React, { useState, useEffect } from 'react';
import Table from '../../Components/Table';
import { SCACColumns } from '../../ColumnsConfig/SCACColumns';
import './../../App.css';
import { Box, Button, Chip, IconButton, InputBase, Paper } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import SCACFormDialog from '../../Modals/SCACFormModal';
import ConfirmationModal from '../../Common/ConfirmationModal';
import SCACService from '../../Services/SCACService';
import loaderGif from "../../Assests/loader.gif";
import { NotificationContainer, NotificationManager } from 'react-notifications';
import 'react-notifications/lib/notifications.css';

const SCAC = () => {
  const [searchValue, setSearchValue] = useState('');
  const [chipValue, setChipValue] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [initialData, setInitialData] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortColumn] = useState('');
  const [order, setSortOrder] = useState('asc');
  const [scacCode, setScacCode] = useState('');


  const validSortFields = ['scac_code', 'scac_name', 'scac_type_id', 'phone', 'url'];
  

  const fetchSCACData = async (
    page = currentPage,
    size = pageSize,
    sortByField = sortBy,
    sortOrder = order,
    searchTerm = scacCode
    
  ) => {
    setIsLoading(true);
    try {
      const queryObj = {
        page,
        pageSize: size,
        sortBy: validSortFields.includes(sortByField) ? sortByField : 'scac_code',
        order: sortOrder,
        searchTerm: searchTerm.trim() || "",
        
      
      };
  
      const response = await SCACService.fetchSCACs(
        queryObj.page,
        queryObj.pageSize,
        queryObj.sortBy,
        queryObj.order,
        queryObj.searchTerm,
      
        
      );
  
      const { data, totalRecords, totalPages } = response;
  
      if (Array.isArray(data)) {
        setRows(data.map(row => ({ ...row, id: row.scac_id })));
        setTotalRecords(totalRecords);
        setTotalPages(totalPages);
      } else {
        throw new Error('Unexpected API response format.');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.ErrorMessage || "Failed to fetch SCAC data.";
      console.error('Failed to fetch SCAC data:', errorMessage);
      NotificationManager.error(errorMessage, "Error", 5000);
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    fetchSCACData();
  }, [currentPage, pageSize]);

  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
    fetchSCACData(page, size, sortBy, order, scacCode);
  };

  //const handleSearchChange = e => setSearchValue(e.target.value);
  const handleSearchChange = e => setSearchValue(e.target.value);

  const handleSort = (field) => {
    let sortField = field;
  
    if (field === 'description') {
      sortField = 'scac_type_id';
    }
  
    if (!validSortFields.includes(sortField)) {
      console.warn(`Invalid sort field: "${sortField}"`);
      return;
    }
  
    const newOrder = sortBy === sortField && order === 'asc' ? 'desc' : 'asc';
    setSortColumn(sortField);
    setSortOrder(newOrder);
  
    fetchSCACData(currentPage, pageSize, sortField, newOrder);
  };
  const handleSearch = () => {
    const trimmedSearchTerm = scacCode.trim(); 
    setChipValue(trimmedSearchTerm);
    fetchSCACData(1, pageSize, sortBy, order, trimmedSearchTerm); 
  };

  const handleChipDelete = async() => {
    setChipValue('');
    try{
      await fetchSCACData(1, pageSize, sortBy, order, '');
  
    }catch(error){
      console.error('Error fetching SCAC data:', error);
      //NotificationManager.error("Failed to fetch SCAC data.", "Error", 5000);
    }finally{
      setScacCode('');
    }
  };

  const handleDialogOpen = () => {
    setInitialData(null);
    setIsDialogOpen(true);
  };

  const handleDialogClose = (event, reason) => {
    if (reason !== 'backdropClick') {
      setIsDialogOpen(false);
    }
  };

  const handleSave = async (formData) => {
      try {
          const { scac_code, scac_name, scac_type_id, phone, url } = formData;
          const dataToSubmit = { scac_code, scac_name, scac_type_id, phone, url };

          if (formData.scac_id) {
              await SCACService.updateSCAC(formData.scac_id, dataToSubmit);
              NotificationManager.success("SCAC Updated successfully.", "Success", 3000);
          } else {
              await SCACService.createSCAC(dataToSubmit);
              NotificationManager.success("SCAC saved successfully.", "Success", 3000);
          }

          fetchSCACData(); // Refresh the table data
          setIsDialogOpen(false);
      } catch (error) {
          const errorMessage = error.response?.data?.ErrorMessage || "Failed to save SCAC.";
          console.error('Error saving SCAC:', errorMessage);
          NotificationManager.error(errorMessage, "Error", 5000);
      }
  };

  const handleEdit = rowData => {
    setInitialData(rowData);
    setIsDialogOpen(true);
  };

  const handleDelete = rowData => {
    setRowToDelete(rowData);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await SCACService.deleteSCAC(rowToDelete.id);
      setRows(rows.filter(row => row.id !== rowToDelete.id));
      setIsDeleteModalOpen(false);
      NotificationManager.success('SCAC deleted successfully.', 'Success', 3000);
    } catch (error) {
      console.error('Error deleting SCAC:', error);
      NotificationManager.error('Failed to delete SCAC.', 'Error', 5000);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
  };

  return (
    <div className="px-5 my-1" style={{ position: 'relative' }}>
      <NotificationContainer />
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center">
          <Paper component="form" className="search-container" onSubmit={(e) => e.preventDefault()}>
            <InputBase
              className="search-input"
              placeholder="Search SCACs"
              inputProps={{ 'aria-label': 'search SCACs' }}
              value={scacCode}
              onChange={(e) => setScacCode(e.target.value)}
            />
            <IconButton type="button" aria-label="search" onClick={handleSearch}>
              <SearchIcon />
            </IconButton>
          </Paper>
          {chipValue && (
            <Chip
              label={chipValue}
              onDelete={handleChipDelete}
              deleteIcon={<CloseIcon />}
              className="chip-style"
            />
          )}
        </Box>
        <Button variant="contained" className="add-new-btn" onClick={handleDialogOpen}>
          Add New SCAC
        </Button>
      </Box>

      <Table
        rows={rows}
        columns={SCACColumns(handleEdit, handleDelete)}
        className="table-header"
        currentPage={currentPage}
        pageSize={pageSize}
        totalRecords={totalRecords}
        onPageChange={handlePageChange}
        totalPages={totalPages}
        getRowId={row => row.id}
        onSort={handleSort}
      />

      {isLoading && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          display="flex"
          justifyContent="center"
          alignItems="center"
          bgcolor="rgba(255, 255, 255, 0.8)"
          zIndex={1}
        >
          <img src={loaderGif} alt="Loading..." width={50} height={50} />
        </Box>
      )}

      <SCACFormDialog
        open={isDialogOpen}
        handleClose={handleDialogClose}
        handleSave={handleSave}
        initialData={initialData}
      />
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        message="Are you sure you want to delete this SCAC?"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};

export default SCAC;
